import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Receipt, Copy, WhatsappLogo, ArrowsClockwise, CheckCircle, Clock, Trash, LinkSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChargesAdmin() {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [phone, setPhone] = useState('');
  const [creating, setCreating] = useState(false);
  const [lastLink, setLastLink] = useState(null);

  const fetchCharges = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/charges`, { withCredentials: true });
      setCharges(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar cobranças');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchCharges(); }, []);

  const linkFor = (id) => `${window.location.origin}/cobranca/${id}`;

  const createCharge = async (e) => {
    e.preventDefault();
    const val = parseFloat(amount);
    if (!clientName.trim() || !val || val <= 0) { toast.error('Preencha nome e valor'); return; }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/admin/charges`,
        { client_name: clientName.trim(), amount: val, description: description.trim(), phone: phone.trim() },
        { withCredentials: true });
      const link = linkFor(data.id);
      setLastLink({ link, name: clientName.trim(), amount: val });
      navigator.clipboard?.writeText(link);
      toast.success('Cobrança criada e link copiado!');
      setClientName(''); setAmount(''); setDescription(''); setPhone('');
      fetchCharges();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao criar'); }
    finally { setCreating(false); }
  };

  const copyLink = (id) => { navigator.clipboard?.writeText(linkFor(id)); toast.success('Link copiado'); };

  const whatsapp = (c) => {
    const msg = encodeURIComponent(`Olá ${c.client_name}! Segue o link para pagamento de $${c.amount?.toFixed(2)}${c.description ? ' ('+c.description+')' : ''}: ${linkFor(c.id)}`);
    const phoneDigits = (c.phone || '').replace(/\D/g, '');
    window.open(phoneDigits ? `https://wa.me/${phoneDigits}?text=${msg}` : `https://wa.me/?text=${msg}`, '_blank');
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir esta cobrança?')) return;
    try { await axios.delete(`${API}/admin/charges/${id}`, { withCredentials: true }); fetchCharges(); }
    catch { toast.error('Erro ao excluir'); }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="charges-admin-page">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A] flex items-center gap-2">
            <Receipt size={26} className="text-[#E85D04]" /> Cobrança Avulsa
          </h1>
          <p className="text-[#73706A] text-sm mt-1">Gere um link de cobrança (Cartão + Zelle) com valor personalizado e envie pro cliente. O link não expira.</p>
        </div>

        <form onSubmit={createCharge} className="bg-white border border-[#E6E2D8] rounded-xl p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Nome do cliente *</Label>
              <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Ex: Carol Zara" data-testid="charge-client-input" />
            </div>
            <div>
              <Label>Valor (USD) *</Label>
              <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Ex: 50.00" data-testid="charge-amount-input" />
            </div>
            <div>
              <Label>Descrição</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ex: Locação quadra 1h" data-testid="charge-desc-input" />
            </div>
            <div>
              <Label>Telefone (WhatsApp, opcional)</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Ex: 15618596830" data-testid="charge-phone-input" />
            </div>
          </div>
          <Button type="submit" disabled={creating} className="bg-[#E85D04] hover:bg-[#DC2F02] text-white" data-testid="charge-create-btn">
            <LinkSimple size={18} className="mr-2" /> {creating ? 'Gerando...' : 'Gerar link de cobrança'}
          </Button>
        </form>

        {lastLink && (
          <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl p-4" data-testid="charge-last-link">
            <p className="text-sm text-[#14532D] font-semibold">Link gerado para {lastLink.name} (${lastLink.amount.toFixed(2)}):</p>
            <div className="flex items-center gap-2 mt-2 bg-white rounded-lg px-3 py-2 border border-[#BBF7D0]">
              <span className="text-sm text-[#1A1A1A] truncate flex-1">{lastLink.link}</span>
              <button onClick={() => { navigator.clipboard?.writeText(lastLink.link); toast.success('Copiado'); }} className="p-2 rounded-lg hover:bg-[#DCFCE7]"><Copy size={18} /></button>
            </div>
          </div>
        )}

        <div className="bg-white border border-[#E6E2D8] rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#E6E2D8] flex items-center justify-between">
            <span className="font-semibold text-[#1A1A1A]">{loading ? 'Carregando...' : `${charges.length} cobrança(s)`}</span>
            <Button variant="outline" size="sm" onClick={fetchCharges} data-testid="charges-refresh-btn"><ArrowsClockwise size={16} className="mr-1" /> Atualizar</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF8F3] text-[#73706A]">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Cliente</th>
                  <th className="text-left px-4 py-3 font-medium">Descrição</th>
                  <th className="text-right px-4 py-3 font-medium">Valor</th>
                  <th className="text-center px-4 py-3 font-medium">Status</th>
                  <th className="text-center px-4 py-3 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {charges.map((c, i) => (
                  <tr key={c.id} className="border-t border-[#F0EDE4]" data-testid={`charge-row-${i}`}>
                    <td className="px-4 py-3 text-[#1A1A1A] font-medium">{c.client_name}</td>
                    <td className="px-4 py-3 text-[#73706A]">{c.description || '—'}</td>
                    <td className="px-4 py-3 text-right text-[#1A1A1A]">${c.amount?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-center">
                      {c.payment_status === 'pago' ? (
                        <span className="inline-flex items-center gap-1 text-[#16A34A] font-semibold"><CheckCircle size={16} weight="fill" /> Pago</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#B45309]"><Clock size={16} /> Pendente</span>
                      )}
                      {c.payment_method && <div className="text-xs text-[#9CA3AF] mt-0.5">{c.payment_method}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => copyLink(c.id)} title="Copiar link" className="p-2 rounded-lg hover:bg-[#F5F2EB] text-[#0077B6]" data-testid={`charge-copy-${i}`}><Copy size={16} /></button>
                        <button onClick={() => whatsapp(c)} title="Enviar no WhatsApp" className="p-2 rounded-lg hover:bg-[#F0FDF4] text-[#25D366]" data-testid={`charge-wa-${i}`}><WhatsappLogo size={16} weight="fill" /></button>
                        {c.zelle_receipt_path && (
                          <a href={`${API}/admin/charges/${c.id}/receipt`} target="_blank" rel="noopener noreferrer" title="Ver comprovante Zelle" className="p-2 rounded-lg hover:bg-[#F5F2EB] text-[#2D6A4F]" data-testid={`charge-receipt-${i}`}><Receipt size={16} /></a>
                        )}
                        <button onClick={() => remove(c.id)} title="Excluir" className="p-2 rounded-lg hover:bg-red-50 text-[#9B2226]" data-testid={`charge-delete-${i}`}><Trash size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && charges.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-[#73706A]">Nenhuma cobrança ainda. Crie a primeira acima.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
